import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ExternalLink, Check } from 'lucide-react';
import SuiteIcon from './SuiteIcon';
import BloomingFlowerOverlay from './BloomingFlowerOverlay';
import { APPS, getSuiteFor } from '../config/suiteRegistry';
import { playBloomChime } from '../lib/bloomChime';

import { useLanguage } from '../context/LanguageContext';

/**
 * SuiteSwitcher v4 — Trust Ecosystem Hub (link-only)
 * ──────────────────────────────────────────────────────────────────────────
 * Per the May 8 strategic pivot, LT/TD/TVD/FT are now INDEPENDENT apps each
 * with their own auth. SuiteSwitcher acts purely as a high-end navigational
 * bridge — no SSO, no JWT handoff, no shared sessions.
 *
 * Click flow:
 *   1. Fire bloom chime (within user-gesture for iOS audio unlock)
 *   2. Show 3-second Blooming Flower transition (cinematic premium feel)
 *   3. Open the destination app's marketing/login URL in a new tab
 *
 * No analytics ping, no token mint, no axios call → zero-latency, zero-friction.
 */
const SuiteSwitcher = ({ language = 'ar', current = 'lt', compact = false }) => {
  const { localize } = useLanguage();
  const isAr = language === 'ar';
  const navigate = useNavigate();
  const currentKey = current === 'lwt' ? 'lt' : current;
  const suite = getSuiteFor(currentKey);
  const activeApp = APPS[currentKey] || APPS.lt;

  const [open, setOpen] = useState(false);
  const [bloomTarget, setBloomTarget] = useState(null);  // {app, url}
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // ── Open a sister app — DIRECT LINK with cinematic bloom transition ──
  const openSisterApp = (app) => {
    // Fire chime within the user gesture for iOS Safari audio unlock
    playBloomChime();
    // Trigger the 3-sec bloom — onComplete will open the destination
    setOpen(false);
    setBloomTarget({ app, url: app.href });
  };

  const handleBloomComplete = () => {
    if (!bloomTarget) return;
    const { url } = bloomTarget;
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (!w) window.location.href = url;
    setTimeout(() => setBloomTarget(null), 200);
  };

  return (
    <>
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex items-center gap-2 ${compact ? 'h-8 px-2.5' : 'h-9 px-3'} rounded-full
            bg-white/[0.04] hover:bg-white/[0.10] ring-1 ring-white/10
            text-slate-200 hover:text-white text-[11px] font-bold transition-colors`}
          aria-haspopup="menu"
          aria-expanded={open}
          data-testid="suite-switcher-toggle"
        >
          {/* White circular badge — high contrast in any theme */}
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white shadow-sm ring-1 ring-slate-900/10">
            <SuiteIcon app={activeApp.key} size={16} rounded={false} />
          </span>
          <span className="tracking-wide">{activeApp.short}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              className={`absolute z-50 mt-2 ${isAr ? 'right-0' : 'left-0'}
                w-72 max-w-[calc(100vw-24px)]
                rounded-2xl overflow-hidden
                bg-[rgba(13,17,23,0.85)] backdrop-blur-2xl
                ring-1 ring-white/10 border border-white/5
                shadow-[0_20px_60px_rgba(0,0,0,0.55)]`}
              role="menu"
              data-testid="suite-switcher-dropdown"
            >
              <div className="px-4 py-2.5 border-b border-white/5 bg-gradient-to-r from-indigo-950/40 via-slate-900/30 to-slate-900/30">
                <p className="text-[9px] font-bold tracking-[0.22em] uppercase text-indigo-300/80">
                  Trust Ecosystem
                </p>
                <p className="text-[12px] text-white/85 mt-0.5 leading-tight">
                  {localize({ ar: 'انتقل بين تطبيقاتك بنقرة', en: 'Jump between your apps in one tap' })}
                </p>
              </div>

              <ul className="py-1.5">
                {suite.map((app) => {
                  const active = app.key === currentKey;
                  return (
                    <li key={app.key}>
                      <button
                        type="button"
                        onClick={() => {
                          if (active) { setOpen(false); navigate('/'); return; }
                          openSisterApp(app);
                        }}
                        className={`group w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-${isAr ? 'right' : 'left'} ${active ? 'bg-indigo-500/12' : 'hover:bg-white/[0.05]'}`}
                        data-testid={`suite-switcher-item-${app.key}`}
                      >
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white shadow-sm ring-1 ring-slate-900/10 shrink-0">
                          <SuiteIcon app={app.key} size={22} rounded={false} />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-bold text-white truncate">
                            {app.name}
                            <span className="ms-2 text-[10px] font-mono text-white/40">{app.short}</span>
                          </span>
                          <span className="block text-[11px] text-white/50 truncate">
                            {localize({ ar: app.descAr, en: app.descEn })}
                          </span>
                        </span>
                        {active ? (
                          <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                        ) : (
                          <ExternalLink className="w-3.5 h-3.5 text-white/30 group-hover:text-white/60 shrink-0" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 3-second Blooming-Flower transition before opening the new tab */}
      <AnimatePresence>
        {bloomTarget && (
          <BloomingFlowerOverlay
            target={bloomTarget.app}
            onComplete={handleBloomComplete}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default SuiteSwitcher;
